import { CloudDownload } from '@mui/icons-material';
import { Box, Tooltip, Typography } from '@mui/material';
import type { ReactElement } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { LabeledValue, LabelIdValue } from '~/components/Fields/LabeledValue';
import RawSeriesProperties from '~/components/Header/BreadcrumbNavigation/ImageBreadcrumb/ImageDetails/RawSeriesProperties';
import {
    AvailableImage,
    SessionImageInfo,
} from '~/hooks/image/usePrimaryImage';
import { formatSeriesDate } from '~/utils/date';
import { formatMmAsCmAtScale } from '~/utils/units';

function SessionImageProperties({
    modality,
    info,
}: {
    modality: string;
    info: SessionImageInfo;
}) {
    const intl = useIntl();
    const xResCm = formatMmAsCmAtScale(info.xResMm, 'voxel');
    const yResCm = formatMmAsCmAtScale(info.yResMm, 'voxel');
    const zResCm = formatMmAsCmAtScale(info.zResMm, 'voxel');
    const formattedDate = info.date ? formatSeriesDate(info.date) : undefined;

    return (
        <Box display="flex" flexDirection="column" gap={0.25}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <CloudDownload
                    sx={{
                        fontSize: 14,
                        color: 'primary.main',
                        flexShrink: 0,
                    }}
                />
                <Typography fontSize={11} color="primary.main" fontWeight={500}>
                    <FormattedMessage id="image.availableForDownload.tooltip" />
                </Typography>
            </Box>
            <LabeledValue
                label={intl.formatMessage({ id: 'modality' })}
                value={modality}
                inline
            />
            {formattedDate && (
                <LabeledValue
                    label={intl.formatMessage({ id: 'seriesDate' })}
                    value={formattedDate}
                    sensitive
                    inline
                />
            )}
            <LabeledValue
                label={intl.formatMessage({ id: 'matrix' })}
                value={`${info.xSize} x ${info.ySize} x ${info.zSize}`}
                inline
            />
            <LabeledValue
                label={
                    <>
                        <FormattedMessage id="resolution" /> (cm):
                    </>
                }
                value={`${xResCm} x ${yResCm} x ${zResCm}`}
                inline
            />
            <LabelIdValue
                label={intl.formatMessage({ id: 'frameOfReferenceUid' })}
                id={info.frameOfReferenceUid}
                inline
            />
        </Box>
    );
}

const tooltipSlotProps = {
    popper: {
        modifiers: [
            {
                name: 'offset' as const,
                options: { offset: [-9, 0] },
            },
        ],
    },
    tooltip: {
        sx: (theme: {
            palette: { divider: string; background: { paper: string } };
        }) => ({
            py: 2,
            px: 2,
            borderRadius: 0,
            border: `1px solid ${theme.palette.divider}`,
            borderLeft: 'none',
            backgroundColor: theme.palette.background.paper,
        }),
    },
};

/**
 * The properties of an image with no `Image3D`, beside `children` on hover:
 * a raw series' DICOM properties, with its delete action when given, or the
 * geometry the session gives for an image not downloaded yet.
 */
export default function UnloadedImageTooltip({
    image,
    onDelete,
    children,
}: {
    image: AvailableImage;
    onDelete?: () => void;
    children: ReactElement;
}) {
    const title = image.rawSeries ? (
        <RawSeriesProperties series={image.rawSeries} onDelete={onDelete} />
    ) : image.sessionImageInfo ? (
        <SessionImageProperties
            modality={image.modality}
            info={image.sessionImageInfo}
        />
    ) : (
        ''
    );
    return (
        <Tooltip
            title={title}
            placement="right-start"
            enterDelay={500}
            leaveDelay={200}
            slotProps={tooltipSlotProps}
        >
            {children}
        </Tooltip>
    );
}
